import { useState, useEffect } from "react";

import { API } from "../config";
const FONT = "'DM Sans', sans-serif";

function fmtDataIT(d) {
  if (!d) return "";
  try {
    const [y, m, g] = d.split("-");
    return `${g}/${m}/${y}`;
  } catch { return d; }
}

function fmtTimestamp(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    const gg = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${gg}/${mm} ore ${hh}:${min}`;
  } catch { return ts; }
}

const S = {
  pagina: { padding: "24px 32px", fontFamily: FONT, background: "#FAFBFC", minHeight: "100vh" },
  card: {
    background: "#fff", borderRadius: 12, padding: "20px 24px",
    marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    border: "1px solid #E2E8F0",
  },
  titoloCard: { fontSize: 15, fontWeight: 700, color: "#1E293B", marginBottom: 4 },
  descrizione: { fontSize: 12, color: "#64748B", marginBottom: 14, lineHeight: 1.5 },
  link: {
    display: "inline-block", marginTop: 12, fontSize: 12, fontWeight: 600,
    color: "#2563EB", cursor: "pointer",
    padding: "6px 14px", borderRadius: 6, border: "1px solid #BFDBFE",
    background: "#EFF6FF",
  },
};

// ─── Setup nuova stagione: avanzamento delle 4 fasi (v192) ──────
// Stesse regole della pagina Setup: la Fase 1 e' derivata dalle due date,
// le altre tre sono le spunte salvate su stagioni_leghe.
function oggiIsoDash() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const g = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${g}`;
}

function fasiFatte(r) {
  let n = (r.data_inizio && r.data_fine) ? 1 : 0;
  if (r.fase2_fatta_il) n += 1;
  if (r.fase3_fatta_il) n += 1;
  if (r.fase4_fatta_il) n += 1;
  return n;
}

// Campionati della stagione piu' recente con fasi ancora aperte.
// Esclusi quelli gia' conclusi: la' il setup e' di sola lettura e non c'e'
// piu' niente su cui intervenire.
function campionatiIncompleti(righe) {
  if (!righe || righe.length === 0) return [];
  const oggi = oggiIsoDash();
  const stagioni = [...new Set(righe.map((r) => r.stagione))].sort().reverse();
  const ultima = stagioni[0];
  return righe
    .filter((r) => r.stagione === ultima)
    .filter((r) => !(r.data_fine && oggi > r.data_fine))
    .map((r) => ({
      lega: r.lega,
      stagione: r.stagione,
      fatte: fasiFatte(r),
      // gia' partito: il setup incompleto e' un problema attivo, non un promemoria
      partito: !!(r.data_inizio && oggi >= r.data_inizio),
    }))
    .filter((x) => x.fatte < 4)
    .sort((a, b) =>
      (b.partito ? 1 : 0) - (a.partito ? 1 : 0) ||
      a.fatte - b.fatte ||
      a.lega.localeCompare(b.lega)
    );
}

// ─── Banner "sistema in pausa estiva" (v164) ───────────────────
// Visibile solo quando il backend riporta sistema_off=true (stagione conclusa +
// 7gg grace passati). Informativo, nessuna azione utente, non chiudibile
// (lo stato e' oggettivo, scompare automaticamente al risveglio del sistema).
function BannerPausaEstiva({ visibile, stagioneRiattivazione }) {
  if (!visibile) return null;
  return (
    <div style={{
      background: "#EFF6FF",
      border: "1px solid #93C5FD",
      borderRadius: 12,
      padding: "12px 18px",
      marginBottom: 20,
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 13,
      color: "#1E40AF",
      lineHeight: 1.4,
    }}>
      <span style={{ fontSize: 18, lineHeight: 1 }}>🌴</span>
      <span>
        <strong>Sistema in pausa estiva</strong>
        {stagioneRiattivazione
          ? <> — riattivazione automatica all'avvio della stagione <strong>{stagioneRiattivazione}</strong></>
          : <> — riattivazione automatica all'avvio della nuova stagione</>}
      </span>
    </div>
  );
}

function BannerSetupIncompleto({ incompleti, onNavigate }) {
  if (!incompleti || incompleti.length === 0) return null;
  const n = incompleti.length;
  const stagione = incompleti[0].stagione;
  const partiti = incompleti.filter((x) => x.partito).length;

  return (
    <div style={{
      background: "#FFFBEB",
      border: "1px solid #FCD34D",
      borderRadius: 12,
      padding: "16px 20px",
      marginBottom: 20,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
    }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <span style={{ fontSize: 18, marginTop: 1 }}>⚠️</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#92400E", marginBottom: 4 }}>
            Setup della stagione {stagione} da completare
          </div>
          <div style={{ fontSize: 12, color: "#92400E", opacity: 0.85, marginBottom: 10, lineHeight: 1.5 }}>
            {n === 1
              ? "Un campionato ha ancora delle fasi aperte."
              : `${n} campionati hanno ancora delle fasi aperte.`}
            {partiti > 0 && (
              <> {partiti === 1 ? "Uno di essi è già" : `${partiti} di essi sono già`} partito
              con il setup incompleto: i dati importati possono essere sbagliati.</>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {incompleti.map((x) => (
              <span key={x.lega} style={{
                fontSize: 11,
                background: x.partito ? "#FEE2E2" : "#FEF3C7",
                border: `1px solid ${x.partito ? "#FCA5A5" : "#FCD34D"}`,
                borderRadius: 6, padding: "3px 10px",
                color: x.partito ? "#991B1B" : "#92400E", fontWeight: 600,
              }}>
                {x.lega} · {x.fatte} di 4
              </span>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", flexShrink: 0 }}>
        <span
          style={{ fontSize: 12, fontWeight: 600, color: "#2563EB", cursor: "pointer", whiteSpace: "nowrap" }}
          onClick={() => onNavigate && onNavigate("setup-stagione")}
        >
          Vai a Setup nuova stagione →
        </span>
      </div>
    </div>
  );
}

function BloccoCicloCorrente({ dati, onVaiMonitor }) {
  const ciclo = dati?.ciclo;
  const selezioni = dati?.selezioni_confermate ?? null;

  return (
    <div style={S.card}>
      <div style={S.titoloCard}>🗓 Ciclo corrente</div>
      <div style={S.descrizione}>Il ciclo rappresenta il periodo di campionato attualmente in corso con le partite selezionate dal sistema.</div>

      {!ciclo ? (
        <div style={{ fontSize: 13, color: "#64748B" }}>Caricamento...</div>
      ) : !ciclo.attivo ? (
        <div style={{ fontSize: 13, color: "#94A3B8", fontStyle: "italic" }}>
          Nessun ciclo attivo al momento.
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1E293B" }}>
              {ciclo.tipo} — {fmtDataIT(ciclo.inizio)} › {fmtDataIT(ciclo.fine)}
            </span>
            <span style={{
              fontSize: 12, fontWeight: 600,
              background: selezioni > 0 ? "#D1FAE5" : "#F1F5F9",
              color: selezioni > 0 ? "#065F46" : "#64748B",
              padding: "3px 10px", borderRadius: 20,
            }}>
              {selezioni === null ? "..." : selezioni === 0
                ? "Nessuna selezione confermata"
                : `${selezioni} ${selezioni === 1 ? "selezione confermata" : "selezioni confermate"}`}
            </span>
          </div>
          <span style={S.link} onClick={onVaiMonitor}>
            Vai al Monitor Live →
          </span>
        </div>
      )}
    </div>
  );
}

function BloccoAnomalie({ erroriAttivi, onVaiInterventi }) {
  // Fix v109: legge da /pipeline/errori-attivi (stessa fonte di Monitor Live)
  // invece di dati.anomalie (che filtrava solo il ciclo corrente).
  // Così mostra tutte le anomalie attive finché non vengono risolte,
  // indipendentemente dal ciclo di appartenenza.
  const voci = erroriAttivi
    ? (erroriAttivi.errori || []).map(e => ({ label: e.label, val: e.count }))
    : [];
  const totale = erroriAttivi?.totale || 0;

  return (
    <div style={{
      ...S.card,
      border: !erroriAttivi ? "1px solid #E2E8F0" : totale > 0 ? "1px solid #FCA5A5" : "1px solid #E2E8F0",
      background: !erroriAttivi ? "#fff" : totale > 0 ? "#FFF7F7" : "#fff",
    }}>
      <div style={S.titoloCard}>⚠️ Anomalie da risolvere</div>
      <div style={S.descrizione}>Anomalie attive che richiedono un intervento manuale: quote e risultati da completare o correggere, squadre create con un nome non riconosciuto.</div>

      {!erroriAttivi ? (
        <div style={{ fontSize: 13, color: "#64748B" }}>Caricamento...</div>
      ) : totale === 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>✅</span>
          <span style={{ fontSize: 13, color: "#16A34A", fontWeight: 600 }}>
            Nessuna anomalia rilevata
          </span>
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 4 }}>
            {voci.map(v => (
              <div key={v.label} style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "#FEE2E2", borderRadius: 8, padding: "6px 14px",
              }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: "#DC2626" }}>{v.val}</span>
                <span style={{ fontSize: 12, color: "#7F1D1D" }}>{v.label}</span>
              </div>
            ))}
          </div>
          <span style={{ ...S.link, borderColor: "#FECACA", background: "#FEF2F2", color: "#DC2626" }}
            onClick={onVaiInterventi}>
            Vai a Interventi su DB →
          </span>
        </div>
      )}
    </div>
  );
}

function BloccoStatoPipeline({ dati, onVaiMonitor }) {
  const pipeline = dati?.pipeline;
  const ok = pipeline?.ok ?? null;
  const quando = pipeline?.ultima_esecuzione ? fmtTimestamp(pipeline.ultima_esecuzione) : "N.D.";

  return (
    <div style={{
      ...S.card,
      border: ok === false ? "1px solid #FCA5A5" : "1px solid #E2E8F0",
      background: ok === false ? "#FFF7F7" : "#fff",
    }}>
      <div style={S.titoloCard}>🔄 Stato aggiornamento dati</div>
      <div style={S.descrizione}>Indica se le routine automatiche di aggiornamento dati hanno funzionato correttamente.</div>

      {!pipeline ? (
        <div style={{ fontSize: 13, color: "#64748B" }}>Caricamento...</div>
      ) : (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>{ok ? "🟢" : "🔴"}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: ok ? "#16A34A" : "#DC2626" }}>
                {ok ? "Aggiornamenti regolari" : "Errori rilevati"}
              </div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                Ultima esecuzione: {quando}
              </div>
            </div>
          </div>
          {ok === false && (
            <span style={{ ...S.link, borderColor: "#FECACA", background: "#FEF2F2", color: "#DC2626" }}
              onClick={onVaiMonitor}>
              Vai al Monitor Live →
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const [dati, setDati] = useState(null);
  const [erroriAttivi, setErroriAttivi] = useState(null);
  const [stagioniLeghe, setStagioniLeghe] = useState([]);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    fetch(`${API}/dashboard`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setDati(d))
      .catch(e => setErrore(`Errore caricamento dati: ${e}`));

    // Fix v109: usa stessa fonte di Monitor Live per coerenza (errori attivi
    // finché non risolti, indipendentemente dal ciclo)
    fetch(`${API}/pipeline/errori-attivi`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setErroriAttivi(d))
      .catch(() => setErroriAttivi({ errori: [], totale: 0 }));

    // v192: avanzamento del setup nuova stagione, per il riquadro in cima
    fetch(`${API}/impostazioni/stagioni-leghe`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => setStagioniLeghe(d || []))
      .catch(() => setStagioniLeghe([]));
  }, []);

  const incompleti = campionatiIncompleti(stagioniLeghe);

  return (
    <div style={S.pagina}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1E293B", marginBottom: 4, letterSpacing: "-0.02em" }}>
        📊 Dashboard
      </h1>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 24 }}>
        Panoramica rapida dello stato della piattaforma.
      </p>

      {errore && (
        <div style={{ fontSize: 13, color: "#DC2626", marginBottom: 16 }}>⚠️ {errore}</div>
      )}

      <BannerPausaEstiva
        visibile={!!dati?.sistema_off}
        stagioneRiattivazione={dati?.stagione_riattivazione}
      />

      <BannerSetupIncompleto incompleti={incompleti} onNavigate={onNavigate} />

      <BloccoCicloCorrente
        dati={dati}
        onVaiMonitor={() => onNavigate && onNavigate("monitor-live")}
      />
      <BloccoAnomalie
        erroriAttivi={erroriAttivi}
        onVaiInterventi={() => onNavigate && onNavigate("interventi-db")}
      />
      <BloccoStatoPipeline dati={dati} onVaiMonitor={() => onNavigate && onNavigate("monitor-live")} />
    </div>
  );
}
